"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CsvParserStream = void 0;
const string_decoder_1 = require("string_decoder");
const stream_1 = require("stream");
const transforms_1 = require("./transforms");
const parser_1 = require("./parser");
class CsvParserStream extends stream_1.Transform {
  constructor(parserOptions) {
    super({
      objectMode: parserOptions.objectMode
    });
    this.lines = '';
    this.rowCount = 0;
    this.parsedRowCount = 0;
    this.parsedLineCount = 0;
    this.endEmitted = false;
    this.headersEmitted = false;
    this.parserOptions = parserOptions;
    this.parser = new parser_1.Parser(parserOptions);
    this.headerTransformer = new transforms_1.HeaderTransformer(parserOptions);
    this.decoder = new string_decoder_1.StringDecoder(parserOptions.encoding);
    this.rowTransformerValidator = new transforms_1.RowTransformerValidator();
  }
  get hasHitRowLimit() {
    return this.parserOptions.limitRows && this.rowCount >= this.parserOptions.maxRows;
  }
  get shouldEmitRows() {
    return this.parsedRowCount > this.parserOptions.skipRows;
  }
  get shouldSkipLine() {
    return this.parsedLineCount <= this.parserOptions.skipLines;
  }
  transform(transformFunction) {
    this.rowTransformerValidator.rowTransform = transformFunction;
    return this;
  }
  validate(validateFunction) {
    this.rowTransformerValidator.rowValidator = validateFunction;
    return this;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emit(event) {
    if (event === 'end') {
      if (!this.endEmitted) {
        this.endEmitted = true;
        super.emit('end', this.rowCount);
      }
      return false;
    }
    for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      rest[_key - 1] = arguments[_key];
    }
    return super.emit(event, ...rest);
  }
  _transform(data, encoding, done) {
    // if we have hit our maxRows parsing limit then skip parsing
    if (this.hasHitRowLimit) {
      return done();
    }
    const wrappedCallback = CsvParserStream.wrapDoneCallback(done);
    try {
      const {
        lines
      } = this;
      const newLine = lines + this.decoder.write(data);
      const rows = this.parse(newLine, true);
      return this.processRows(rows, wrappedCallback);
    } catch (e) {
      return wrappedCallback(e);
    }
  }
  _flush(done) {
    const wrappedCallback = CsvParserStream.wrapDoneCallback(done);
    // if we have hit our maxRows parsing limit then skip parsing
    if (this.hasHitRowLimit) {
      return wrappedCallback();
    }
    try {
      const newLine = this.lines + this.decoder.end();
      const rows = this.parse(newLine, false);
      return this.processRows(rows, wrappedCallback);
    } catch (e) {
      return wrappedCallback(e);
    }
  }
  parse(data, hasMoreData) {
    if (!data) {
      return [];
    }
    const {
      line,
      rows
    } = this.parser.parse(data, hasMoreData);
    this.lines = line;
    return rows;
  }
  processRows(rows, cb) {
    const rowsLength = rows.length;
    const iterate = i => {
      const callNext = err => {
        if (err) {
          return cb(err);
        }
        if (i % 100 === 0) {
          // incase the transform are sync insert a next tick to prevent stack overflow
          setImmediate(() => iterate(i + 1));
          return undefined;
        }
        return iterate(i + 1);
      };
      this.checkAndEmitHeaders();
      // if we have emitted all rows or we have hit the maxRows limit option
      // then end
      if (i >= rowsLength || this.hasHitRowLimit) {
        return cb();
      }
      this.parsedLineCount += 1;
      if (this.shouldSkipLine) {
        return callNext();
      }
      const row = rows[i];
      this.rowCount += 1;
      this.parsedRowCount += 1;
      const nextRowCount = this.rowCount;
      return this.transformRow(row, (err, transformResult) => {
        if (err) {
          this.rowCount -= 1;
          return callNext(err);
        }
        if (!transformResult) {
          return callNext(new Error('expected transform result'));
        }
        if (!transformResult.isValid) {
          this.emit('data-invalid', transformResult.row, nextRowCount, transformResult.reason);
        } else if (transformResult.row) {
          return this.pushRow(transformResult.row, callNext);
        }
        return callNext();
      });
    };
    iterate(0);
  }
  transformRow(parsedRow, cb) {
    try {
      this.headerTransformer.transform(parsedRow, (err, withHeaders) => {
        if (err) {
          return cb(err);
        }
        if (!withHeaders) {
          return cb(new Error('Expected result from header transform'));
        }
        if (!withHeaders.isValid) {
          if (this.shouldEmitRows) {
            return cb(null, {
              isValid: false,
              row: parsedRow
            });
          }
          // skipped because of skipRows option remove from total row count
          return this.skipRow(cb);
        }
        if (withHeaders.row) {
          if (this.shouldEmitRows) {
            return this.rowTransformerValidator.transformAndValidate(withHeaders.row, cb);
          }
          // skipped because of skipRows option remove from total row count
          return this.skipRow(cb);
        }
        // this is a header row dont include in the rowCount or parsedRowCount
        this.rowCount -= 1;
        this.parsedRowCount -= 1;
        return cb(null, {
          row: null,
          isValid: true
        });
      });
    } catch (e) {
      cb(e);
    }
  }
  checkAndEmitHeaders() {
    if (!this.headersEmitted && this.headerTransformer.headers) {
      this.headersEmitted = true;
      this.emit('headers', this.headerTransformer.headers);
    }
  }
  skipRow(cb) {
    // skipped because of skipRows option remove from total row count
    this.rowCount -= 1;
    return cb(null, {
      row: null,
      isValid: true
    });
  }
  pushRow(row, cb) {
    try {
      if (!this.parserOptions.objectMode) {
        this.push(JSON.stringify(row));
      } else {
        this.push(row);
      }
      cb();
    } catch (e) {
      cb(e);
    }
  }
  static wrapDoneCallback(done) {
    let errorCalled = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (err) {
      if (err) {
        if (errorCalled) {
          throw err;
        }
        errorCalled = true;
        done(err);
        return;
      }
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      done(...args);
    };
  }
}
exports.CsvParserStream = CsvParserStream;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJuYW1lcyI6WyJzdHJpbmdfZGVjb2Rlcl8xIiwicmVxdWlyZSIsInN0cmVhbV8xIiwidHJhbnNmb3Jtc18xIiwicGFyc2VyXzEiLCJDc3ZQYXJzZXJTdHJlYW0iLCJUcmFuc2Zvcm0iLCJjb25zdHJ1Y3RvciIsInBhcnNlck9wdGlvbnMiLCJvYmplY3RNb2RlIiwibGluZXMiLCJyb3dDb3VudCIsInBhcnNlZFJvd0NvdW50IiwicGFyc2VkTGluZUNvdW50IiwiZW5kRW1pdHRlZCIsImhlYWRlcnNFbWl0dGVkIiwicGFyc2VyIiwiUGFyc2VyIiwiaGVhZGVyVHJhbnNmb3JtZXIiLCJIZWFkZXJUcmFuc2Zvcm1lciIsImRlY29kZXIiLCJTdHJpbmdEZWNvZGVyIiwiZW5jb2RpbmciLCJyb3dUcmFuc2Zvcm1lclZhbGlkYXRvciIsIlJvd1RyYW5zZm9ybWVyVmFsaWRhdG9yIiwiaGFzSGl0Um93TGltaXQiLCJsaW1pdFJvd3MiLCJtYXhSb3dzIiwic2hvdWxkRW1pdFJvd3MiLCJza2lwUm93cyIsInNob3VsZFNraXBMaW5lIiwic2tpcExpbmVzIiwidHJhbnNmb3JtIiwidHJhbnNmb3JtRnVuY3Rpb24iLCJyb3dUcmFuc2Zvcm0iLCJ2YWxpZGF0ZSIsInZhbGlkYXRlRnVuY3Rpb24iLCJyb3dWYWxpZGF0b3IiLCJlbWl0IiwiZXZlbnQiLCJfbGVuIiwiYXJndW1lbnRzIiwibGVuZ3RoIiwicmVzdCIsIkFycmF5IiwiX2tleSIsIl90cmFuc2Zvcm0iLCJkYXRhIiwiZG9uZSIsIndyYXBwZWRDYWxsYmFjayIsIndyYXBEb25lQ2FsbGJhY2siLCJuZXdMaW5lIiwid3JpdGUiLCJyb3dzIiwicGFyc2UiLCJwcm9jZXNzUm93cyIsImUiLCJfZmx1c2giLCJlbmQiLCJoYXNNb3JlRGF0YSIsImxpbmUiLCJjYiIsInJvd3NMZW5ndGgiLCJpdGVyYXRlIiwiaSIsImNhbGxOZXh0IiwiZXJyIiwic2V0SW1tZWRpYXRlIiwidW5kZWZpbmVkIiwiY2hlY2tBbmRFbWl0SGVhZGVycyIsInJvdyIsIm5leHRSb3dDb3VudCIsInRyYW5zZm9ybVJvdyIsInRyYW5zZm9ybVJlc3VsdCIsIkVycm9yIiwiaXNWYWxpZCIsInJlYXNvbiIsInB1c2hSb3ciLCJwYXJzZWRSb3ciLCJ3aXRoSGVhZGVycyIsInNraXBSb3ciLCJ0cmFuc2Zvcm1BbmRWYWxpZGF0ZSIsImhlYWRlcnMiLCJwdXNoIiwiSlNPTiIsInN0cmluZ2lmeSIsImVycm9yQ2FsbGVkIiwiX2xlbjIiLCJhcmdzIiwiX2tleTIiLCJleHBvcnRzIl0sInNvdXJjZXMiOlsiLi4vLi4vc3JjL0NzdlBhcnNlclN0cmVhbS50cyJdLCJzb3VyY2VzQ29udGVudCI6W251bGxdLCJtYXBwaW5ncyI6Ijs7Ozs7O0FBQUEsTUFBQUEsZ0JBQUEsR0FBQUMsT0FBQTtBQUNBLE1BQUFDLFFBQUEsR0FBQUQsT0FBQTtBQUVBLE1BQUFFLFlBQUEsR0FBQUYsT0FBQTtBQUNBLE1BQUFHLFFBQUEsR0FBQUgsT0FBQTtBQUdBLE1BQWFJLGVBQThDLFNBQVFILFFBQUEsQ0FBQUksU0FBUztFQXVCeEVDLFlBQW1CQyxhQUE0QjtJQUMzQyxLQUFLLENBQUM7TUFBRUMsVUFBVSxFQUFFRCxhQUFhLENBQUNDO0lBQVUsQ0FBRSxDQUFDO0lBYjNDLEtBQUFDLEtBQUssR0FBRyxFQUFFO0lBRVYsS0FBQUMsUUFBUSxHQUFHLENBQUM7SUFFWixLQUFBQyxjQUFjLEdBQUcsQ0FBQztJQUVsQixLQUFBQyxlQUFlLEdBQUcsQ0FBQztJQUVuQixLQUFBQyxVQUFVLEdBQUcsS0FBSztJQUVsQixLQUFBQyxjQUFjLEdBQUcsS0FBSztJQUkxQixJQUFJLENBQUNQLGFBQWEsR0FBR0EsYUFBYTtJQUNsQyxJQUFJLENBQUNRLE1BQU0sR0FBRyxJQUFJWixRQUFBLENBQUFhLE1BQU0sQ0FBQ1QsYUFBYSxDQUFDO0lBQ3ZDLElBQUksQ0FBQ1UsaUJBQWlCLEdBQUcsSUFBSWYsWUFBQSxDQUFBZ0IsaUJBQWlCLENBQUNYLGFBQWEsQ0FBQztJQUM3RCxJQUFJLENBQUNZLE9BQU8sR0FBRyxJQUFJcEIsZ0JBQUEsQ0FBQXFCLGFBQWEsQ0FBQ2IsYUFBYSxDQUFDYyxRQUFRLENBQUM7SUFDeEQsSUFBSSxDQUFDQyx1QkFBdUIsR0FBRyxJQUFJcEIsWUFBQSxDQUFBcUIsdUJBQXVCLEVBQUU7RUFDaEU7RUFFQSxJQUFZQyxjQUFjQSxDQUFBO0lBQ3RCLE9BQU8sSUFBSSxDQUFDakIsYUFBYSxDQUFDa0IsU0FBUyxJQUFJLElBQUksQ0FBQ2YsUUFBUSxJQUFJLElBQUksQ0FBQ0gsYUFBYSxDQUFDbUIsT0FBTztFQUN0RjtFQUVBLElBQVlDLGNBQWNBLENBQUE7SUFDdEIsT0FBTyxJQUFJLENBQUNoQixjQUFjLEdBQUcsSUFBSSxDQUFDSixhQUFhLENBQUNxQixRQUFRO0VBQzVEO0VBRUEsSUFBWUMsY0FBY0EsQ0FBQTtJQUN0QixPQUFPLElBQUksQ0FBQ2pCLGVBQWUsSUFBSSxJQUFJLENBQUNMLGFBQWEsQ0FBQ3VCLFNBQVM7RUFDL0Q7RUFFT0MsU0FBU0EsQ0FBQ0MsaUJBQTZDO0lBQzFELElBQUksQ0FBQ1YsdUJBQXVCLENBQUNXLFlBQVksR0FBR0QsaUJBQWlCO0lBQzdELE9BQU8sSUFBSTtFQUNmO0VBRU9FLFFBQVFBLENBQUNDLGdCQUFnQztJQUM1QyxJQUFJLENBQUNiLHVCQUF1QixDQUFDYyxZQUFZLEdBQUdELGdCQUFnQjtJQUM1RCxPQUFPLElBQUk7RUFDZjtFQUVBO0VBQ09FLElBQUlBLENBQUNDLEtBQXNCLEVBQWdCO0lBQzlDLElBQUlBLEtBQUssS0FBSyxLQUFLLEVBQUU7TUFDakIsSUFBSSxDQUFDLElBQUksQ0FBQ3pCLFVBQVUsRUFBRTtRQUNsQixJQUFJLENBQUNBLFVBQVUsR0FBRyxJQUFJO1FBQ3RCLEtBQUssQ0FBQ3dCLElBQUksQ0FBQyxLQUFLLEVBQUUsSUFBSSxDQUFDM0IsUUFBUSxDQUFDOztNQUVwQyxPQUFPLEtBQUs7O0lBQ2YsU0FBQTZCLElBQUEsR0FBQUMsU0FBQSxDQUFBQyxNQUFBLEVBUGtDQyxJQUFXLE9BQUFDLEtBQUEsQ0FBQUosSUFBQSxPQUFBQSxJQUFBLFdBQUFLLElBQUEsTUFBQUEsSUFBQSxHQUFBTCxJQUFBLEVBQUFLLElBQUE7TUFBWEYsSUFBVyxDQUFBRSxJQUFBLFFBQUFKLFNBQUEsQ0FBQUksSUFBQTtJQUFBO0lBUTlDLE9BQU8sS0FBSyxDQUFDUCxJQUFJLENBQUNDLEtBQUssRUFBRSxHQUFHSSxJQUFJLENBQUM7RUFDckM7RUFFT0csVUFBVUEsQ0FBQ0MsSUFBWSxFQUFFekIsUUFBZ0IsRUFBRTBCLElBQXVCO0lBQ3JFO0lBQ0EsSUFBSSxJQUFJLENBQUN2QixjQUFjLEVBQUU7TUFDckIsT0FBT3VCLElBQUksRUFBRTs7SUFFakIsTUFBTUMsZUFBZSxHQUFHNUMsZUFBZSxDQUFDNkMsZ0JBQWdCLENBQUNGLElBQUksQ0FBQztJQUM5RCxJQUFJO01BQ0EsTUFBTTtRQUFFdEM7TUFBSyxDQUFFLEdBQUcsSUFBSTtNQUN0QixNQUFNeUMsT0FBTyxHQUFHekMsS0FBSyxHQUFHLElBQUksQ0FBQ1UsT0FBTyxDQUFDZ0MsS0FBSyxDQUFDTCxJQUFJLENBQUM7TUFDaEQsTUFBTU0sSUFBSSxHQUFHLElBQUksQ0FBQ0MsS0FBSyxDQUFDSCxPQUFPLEVBQUUsSUFBSSxDQUFDO01BQ3RDLE9BQU8sSUFBSSxDQUFDSSxXQUFXLENBQUNGLElBQUksRUFBRUosZUFBZSxDQUFDO0tBQ2pELENBQUMsT0FBT08sQ0FBQyxFQUFFO01BQ1IsT0FBT1AsZUFBZSxDQUFDTyxDQUFDLENBQUM7O0VBRWpDO0VBRU9DLE1BQU1BLENBQUNULElBQXVCO0lBQ2pDLE1BQU1DLGVBQWUsR0FBRzVDLGVBQWUsQ0FBQzZDLGdCQUFnQixDQUFDRixJQUFJLENBQUM7SUFDOUQ7SUFDQSxJQUFJLElBQUksQ0FBQ3ZCLGNBQWMsRUFBRTtNQUNyQixPQUFPd0IsZUFBZSxFQUFFOztJQUU1QixJQUFJO01BQ0EsTUFBTUUsT0FBTyxHQUFHLElBQUksQ0FBQ3pDLEtBQUssR0FBRyxJQUFJLENBQUNVLE9BQU8sQ0FBQ3NDLEdBQUcsRUFBRTtNQUMvQyxNQUFNTCxJQUFJLEdBQUcsSUFBSSxDQUFDQyxLQUFLLENBQUNILE9BQU8sRUFBRSxLQUFLLENBQUM7TUFDdkMsT0FBTyxJQUFJLENBQUNJLFdBQVcsQ0FBQ0YsSUFBSSxFQUFFSixlQUFlLENBQUM7S0FDakQsQ0FBQyxPQUFPTyxDQUFDLEVBQUU7TUFDUixPQUFPUCxlQUFlLENBQUNPLENBQUMsQ0FBQzs7RUFFakM7RUFFUUYsS0FBS0EsQ0FBQ1AsSUFBWSxFQUFFWSxXQUFvQjtJQUM1QyxJQUFJLENBQUNaLElBQUksRUFBRTtNQUNQLE9BQU8sRUFBRTs7SUFFYixNQUFNO01BQUVhLElBQUk7TUFBRVA7SUFBSSxDQUFFLEdBQUcsSUFBSSxDQUFDckMsTUFBTSxDQUFDc0MsS0FBSyxDQUFDUCxJQUFJLEVBQUVZLFdBQVcsQ0FBQztJQUMzRCxJQUFJLENBQUNqRCxLQUFLLEdBQUdrRCxJQUFJO0lBQ2pCLE9BQU9QLElBQUk7RUFDZjtFQUVRRSxXQUFXQSxDQUFDRixJQUFnQixFQUFFUSxFQUFxQjtJQUN2RCxNQUFNQyxVQUFVLEdBQUdULElBQUksQ0FBQ1gsTUFBTTtJQUM5QixNQUFNcUIsT0FBTyxHQUFJQyxDQUFTLElBQVU7TUFDaEMsTUFBTUMsUUFBUSxHQUFJQyxHQUFXLElBQVU7UUFDbkMsSUFBSUEsR0FBRyxFQUFFO1VBQ0wsT0FBT0wsRUFBRSxDQUFDSyxHQUFHLENBQUM7O1FBRWxCLElBQUlGLENBQUMsR0FBRyxHQUFHLEtBQUssQ0FBQyxFQUFFO1VBQ2Y7VUFDQUcsWUFBWSxDQUFDLE1BQVlKLE9BQU8sQ0FBQ0MsQ0FBQyxHQUFHLENBQUMsQ0FBQyxDQUFDO1VBQ3hDLE9BQU9JLFNBQVM7O1FBRXBCLE9BQU9MLE9BQU8sQ0FBQ0MsQ0FBQyxHQUFHLENBQUMsQ0FBQztNQUN6QixDQUFDO01BQ0QsSUFBSSxDQUFDSyxtQkFBbUIsRUFBRTtNQUMxQjtNQUNBO01BQ0EsSUFBSUwsQ0FBQyxJQUFJRixVQUFVLElBQUksSUFBSSxDQUFDckMsY0FBYyxFQUFFO1FBQ3hDLE9BQU9vQyxFQUFFLEVBQUU7O01BRWYsSUFBSSxDQUFDaEQsZUFBZSxJQUFJLENBQUM7TUFDekIsSUFBSSxJQUFJLENBQUNpQixjQUFjLEVBQUU7UUFDckIsT0FBT21DLFFBQVEsRUFBRTs7TUFFckIsTUFBTUssR0FBRyxHQUFHakIsSUFBSSxDQUFDVyxDQUFDLENBQUM7TUFDbkIsSUFBSSxDQUFDckQsUUFBUSxJQUFJLENBQUM7TUFDbEIsSUFBSSxDQUFDQyxjQUFjLElBQUksQ0FBQztNQUN4QixNQUFNMkQsWUFBWSxHQUFHLElBQUksQ0FBQzVELFFBQVE7TUFDbEMsT0FBTyxJQUFJLENBQUM2RCxZQUFZLENBQUNGLEdBQUcsRUFBRSxDQUFDSixHQUFHLEVBQUVPLGVBQWUsS0FBVTtRQUN6RCxJQUFJUCxHQUFHLEVBQUU7VUFDTCxJQUFJLENBQUN2RCxRQUFRLElBQUksQ0FBQztVQUNsQixPQUFPc0QsUUFBUSxDQUFDQyxHQUFHLENBQUM7O1FBRXhCLElBQUksQ0FBQ08sZUFBZSxFQUFFO1VBQ2xCLE9BQU9SLFFBQVEsQ0FBQyxJQUFJUyxLQUFLLENBQUMsMkJBQTJCLENBQUMsQ0FBQzs7UUFFM0QsSUFBSSxDQUFDRCxlQUFlLENBQUNFLE9BQU8sRUFBRTtVQUMxQixJQUFJLENBQUNyQyxJQUFJLENBQUMsY0FBYyxFQUFFbUMsZUFBZSxDQUFDSCxHQUFHLEVBQUVDLFlBQVksRUFBRUUsZUFBZSxDQUFDRyxNQUFNLENBQUM7U0FDdkYsTUFBTSxJQUFJSCxlQUFlLENBQUNILEdBQUcsRUFBRTtVQUM1QixPQUFPLElBQUksQ0FBQ08sT0FBTyxDQUFDSixlQUFlLENBQUNILEdBQUcsRUFBRUwsUUFBUSxDQUFDOztRQUV0RCxPQUFPQSxRQUFRLEVBQUU7TUFDckIsQ0FBQyxDQUFDO0lBQ04sQ0FBQztJQUNERixPQUFPLENBQUMsQ0FBQyxDQUFDO0VBQ2Q7RUFFUVMsWUFBWUEsQ0FBQ00sU0FBbUIsRUFBRWpCLEVBQTJCO0lBQ2pFLElBQUk7TUFDQSxJQUFJLENBQUMzQyxpQkFBaUIsQ0FBQ2MsU0FBUyxDQUFDOEMsU0FBUyxFQUFFLENBQUNaLEdBQUcsRUFBRWEsV0FBVyxLQUFVO1FBQ25FLElBQUliLEdBQUcsRUFBRTtVQUNMLE9BQU9MLEVBQUUsQ0FBQ0ssR0FBRyxDQUFDOztRQUVsQixJQUFJLENBQUNhLFdBQVcsRUFBRTtVQUNkLE9BQU9sQixFQUFFLENBQUMsSUFBSWEsS0FBSyxDQUFDLHVDQUF1QyxDQUFDLENBQUM7O1FBRWpFLElBQUksQ0FBQ0ssV0FBVyxDQUFDSixPQUFPLEVBQUU7VUFDdEIsSUFBSSxJQUFJLENBQUMvQyxjQUFjLEVBQUU7WUFDckIsT0FBT2lDLEVBQUUsQ0FBQyxJQUFJLEVBQUU7Y0FBRWMsT0FBTyxFQUFFLEtBQUs7Y0FBRUwsR0FBRyxFQUFHUTtZQUF3QixDQUFFLENBQUM7O1VBRXZFO1VBQ0EsT0FBTyxJQUFJLENBQUNFLE9BQU8sQ0FBQ25CLEVBQUUsQ0FBQzs7UUFFM0IsSUFBSWtCLFdBQVcsQ0FBQ1QsR0FBRyxFQUFFO1VBQ2pCLElBQUksSUFBSSxDQUFDMUMsY0FBYyxFQUFFO1lBQ3JCLE9BQU8sSUFBSSxDQUFDTCx1QkFBdUIsQ0FBQzBELG9CQUFvQixDQUFDRixXQUFXLENBQUNULEdBQUcsRUFBRVQsRUFBRSxDQUFDOztVQUVqRjtVQUNBLE9BQU8sSUFBSSxDQUFDbUIsT0FBTyxDQUFDbkIsRUFBRSxDQUFDOztRQUUzQjtRQUNBLElBQUksQ0FBQ2xELFFBQVEsSUFBSSxDQUFDO1FBQ2xCLElBQUksQ0FBQ0MsY0FBYyxJQUFJLENBQUM7UUFDeEIsT0FBT2lELEVBQUUsQ0FBQyxJQUFJLEVBQUU7VUFBRVMsR0FBRyxFQUFFLElBQUk7VUFBRUssT0FBTyxFQUFFO1FBQUksQ0FBRSxDQUFDO01BQ2pELENBQUMsQ0FBQztLQUNMLENBQUMsT0FBT25CLENBQUMsRUFBRTtNQUNSSyxFQUFFLENBQUNMLENBQUMsQ0FBQzs7RUFFYjtFQUVRYSxtQkFBbUJBLENBQUE7SUFDdkIsSUFBSSxDQUFDLElBQUksQ0FBQ3RELGNBQWMsSUFBSSxJQUFJLENBQUNHLGlCQUFpQixDQUFDZ0UsT0FBTyxFQUFFO01BQ3hELElBQUksQ0FBQ25FLGNBQWMsR0FBRyxJQUFJO01BQzFCLElBQUksQ0FBQ3VCLElBQUksQ0FBQyxTQUFTLEVBQUUsSUFBSSxDQUFDcEIsaUJBQWlCLENBQUNnRSxPQUFPLENBQUM7O0VBRTVEO0VBRVFGLE9BQU9BLENBQUNuQixFQUEyQjtJQUN2QztJQUNBLElBQUksQ0FBQ2xELFFBQVEsSUFBSSxDQUFDO0lBQ2xCLE9BQU9rRCxFQUFFLENBQUMsSUFBSSxFQUFFO01BQUVTLEdBQUcsRUFBRSxJQUFJO01BQUVLLE9BQU8sRUFBRTtJQUFJLENBQUUsQ0FBQztFQUNqRDtFQUVRRSxPQUFPQSxDQUFDUCxHQUFRLEVBQUVULEVBQXlCO0lBQy9DLElBQUk7TUFDQSxJQUFJLENBQUMsSUFBSSxDQUFDckQsYUFBYSxDQUFDQyxVQUFVLEVBQUU7UUFDaEMsSUFBSSxDQUFDMEUsSUFBSSxDQUFDQyxJQUFJLENBQUNDLFNBQVMsQ0FBQ2YsR0FBRyxDQUFDLENBQUM7T0FDakMsTUFBTTtRQUNILElBQUksQ0FBQ2EsSUFBSSxDQUFDYixHQUFHLENBQUM7O01BRWxCVCxFQUFFLEVBQUU7S0FDUCxDQUFDLE9BQU9MLENBQUMsRUFBRTtNQUNSSyxFQUFFLENBQUNMLENBQUMsQ0FBQzs7RUFFYjtFQUVRLE9BQU9OLGdCQUFnQkEsQ0FBQ0YsSUFBdUI7SUFDbkQsSUFBSXNDLFdBQVcsR0FBRyxLQUFLO0lBQ3ZCO0lBQ0EsT0FBTyxVQUFDcEIsR0FBNkIsRUFBMEI7TUFDM0QsSUFBSUEsR0FBRyxFQUFFO1FBQ0wsSUFBSW9CLFdBQVcsRUFBRTtVQUNiLE1BQU1wQixHQUFHOztRQUVib0IsV0FBVyxHQUFHLElBQUk7UUFDbEJ0QyxJQUFJLENBQUNrQixHQUFHLENBQUM7UUFDVDs7TUFDSCxTQUFBcUIsS0FBQSxHQUFBOUMsU0FBQSxDQUFBQyxNQUFBLEVBUnFDOEMsSUFBVyxPQUFBNUMsS0FBQSxDQUFBMkMsS0FBQSxPQUFBQSxLQUFBLFdBQUFFLEtBQUEsTUFBQUEsS0FBQSxHQUFBRixLQUFBLEVBQUFFLEtBQUE7UUFBWEQsSUFBVyxDQUFBQyxLQUFBLFFBQUFoRCxTQUFBLENBQUFnRCxLQUFBO01BQUE7TUFTakR6QyxJQUFJLENBQUMsR0FBR3dDLElBQUksQ0FBQztJQUNqQixDQUFDO0VBQ0w7O0FBbE9KRSxPQUFBLENBQUFyRixlQUFBLEdBQUFBLGVBQUEifQ==